import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { Typography, CircularProgress, TextField } from '@material-ui/core';
import { useLazyQuery, useMutation } from 'react-apollo';
import { FIND_ALL_PAGAMENTOS_PROFISSIONAL_SAUDE } from '../../../../graphql/queryes';
import { UPDATE_OBSERVACAO_PAGAMENTO_PROFISSIONAL } from '../../../../graphql/mutations';
import Notification from '../../../../components/notification/Notification';
import Button from '../../../../components/button';
import moment from 'moment';
import Number from '../../../../utils/number';

const notificationDefault = {
  variant: '',
  message: '',
  isOpen: false,
};

const PagamentosProfissional = ({ profissional, onClose }) => {
  const [pagamentos, setPagamentos] = useState([]);
  const [notification, setNotification] = useState({
    ...notificationDefault,
  });

  const [loadPagamentos, { data, loading }] = useLazyQuery(
    FIND_ALL_PAGAMENTOS_PROFISSIONAL_SAUDE,
    {
      fetchPolicy: 'no-cache',
    }
  );

  const [updateObservacaoPagamento, { loading: loadingSave }] = useMutation(
    UPDATE_OBSERVACAO_PAGAMENTO_PROFISSIONAL
  );

  useEffect(() => {

    if (profissional?.id) {
      const fetchPagamentos = async () => {
        try {
           loadPagamentos({
            variables: {
              profissionalSaudeId: profissional.id,
            },
          });
        } catch (error) {
          setNotification({
            variant: 'error',
            isOpen: true,
            message: 'Erro ao carregar pagamentos do profissional.',
          });
        }
      };
      fetchPagamentos();
    }
  }, [profissional, loadPagamentos]);

  useEffect(() => {
    if (data?.findAllProfissionalSaudeClientePagamento) {
      setPagamentos(data.findAllProfissionalSaudeClientePagamento);
    }
  }, [data]);

  const handleObservacaoChange = (pagamentoId, value) => {
    setPagamentos((prev) =>
      prev.map((pagamento) =>
        pagamento.id === pagamentoId
          ? { ...pagamento, pagamento: { ...pagamento.pagamento, observacao: value } }
          : pagamento
      )
    );
  };

  const handleSalvar = async () => {
    try {

      const variables = {
        pagamentos: pagamentos.map((item) => {

          return {
            id: item.pagamento?.id,
            observacao: item.pagamento.observacao
          }
        }
        )
      }

      await updateObservacaoPagamento({ variables });

      setNotification({
        variant: 'success',
        isOpen: true,
        message: 'Observações salvas com sucesso!',
      });
      onClose()
    } catch (error) {
      setNotification({
        variant: 'error',
        isOpen: true,
        message: 'Erro ao salvar observações.',
      });
    }
  };

  return (
    <Container>
      <Header>
        <Typography variant="h6">{profissional?.nome}</Typography>
        <Typography variant="body2" color="textSecondary">
          CPF: {profissional?.cpf}
        </Typography>
      </Header>

      {loading ? (
        <LoadingContainer>
          <CircularProgress />
        </LoadingContainer>
      ) : (
        <ScrollContainer>
          <PagamentosList>
            {pagamentos?.length === 0 ? (
              <EmptyState>
                <Typography>Nenhum pagamento encontrado</Typography>
              </EmptyState>
            ) : (
              pagamentos?.map((pagamento, index) => (
                <PagamentoItem key={index}>
                  <PagamentoHeader>
                    <PagamentoField>
                      <strong>Data:</strong>{' '}
                      {moment(pagamento.data).format('DD/MM/YYYY')}
                    </PagamentoField>
                    <PagamentoField>
                      <strong>Valor:</strong>{' '}
                      {Number.currencyFormat(pagamento.valor)}
                    </PagamentoField>
                    <PagamentoField>
                      <strong>Status:</strong> {pagamento.status}
                    </PagamentoField>
                  </PagamentoHeader>
                  <ObservacaoContainer>
                    <TextField
                      label="Observação"
                      multiline
                      rows={2}
                      variant="outlined"
                      fullWidth
                      value={pagamento.pagamento.observacao || ''}
                      onChange={(e) =>
                        handleObservacaoChange(pagamento.id, e.target.value)
                      }
                      size="small"
                    />
                  </ObservacaoContainer>
                </PagamentoItem>
              ))
            )}
          </PagamentosList>
        </ScrollContainer>
      )}

      <SaveButtonContainer>
        <Button onClick={handleSalvar} disabled={loadingSave}>
          Salvar
          {loadingSave && (
            <CircularProgress
              size={16}
              color="inherit"
              style={{ marginLeft: 8 }}
            />
          )}
        </Button>
      </SaveButtonContainer>

      <Notification
        close={() => {
          setNotification({
            ...notificationDefault,
          });
        }}
        reset={() => {
          setNotification({
            ...notificationDefault,
          });
        }}
        isOpen={notification.isOpen}
        variant={notification.variant}
        message={notification.message}
      />
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: calc(100vh - 106px);
  padding: 0 20px 20px;
`;

const Header = styled.div`
  margin-bottom: 20px;
  border-bottom: 1px solid #e0e0e0;
  padding-bottom: 10px;
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  gap: 16px;
`;

const ScrollContainer = styled.div`
  flex: 1;
  overflow-y: auto;
`;

const PagamentosList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const PagamentoItem = styled.div`
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 16px;
  background-color: #f9f9f9;
`;

const PagamentoHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  gap: 16px;
`;

const PagamentoField = styled.div`
  font-size: 14px;
  color: #333;
`;

const ObservacaoContainer = styled.div`
  margin-top: 8px;
`;

const SaveButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  padding-top: 16px;
`;

const EmptyState = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px;
  color: #666;
`;

export default PagamentosProfissional;
